import { useEffect, useRef, useState } from "react";
import { Autocomplete, Box, Button, Popper, TextField } from "@mui/material";
import { useDispatch } from "react-redux";
import { searchPPContractor } from "../../../../store/actions/privateProviderActions";
import { useFormikContext } from "formik";

const CustomPopper = (props) => (
  <Popper
    {...props}
    modifiers={[
      {
        name: "offset",
        options: {
          offset: [0, 4],
        },
      },
    ]}
    sx={{
      "& .MuiAutocomplete-paper": {
        marginTop: "1.5px",
        maxHeight: 220,
        width: "auto",
        maxWidth: "100%",
        overflowY: "auto", // Ensure only one scrollbar appears
      },
    }}
  />
);

const LicenseAuto = ({
  setFoundContractor,
  setShowAddInput,
  setShowLicenseInput,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const [contractorOptions, setContractorOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);

  const debounceTimerRef = useRef(null);
  const suppressFetchRef = useRef(false);
  const lastFetchedContractorRef = useRef(null);
  const formik = useFormikContext();

  const dispatch = useDispatch();

  const formatContractorOption = (option) => {
    if (!option || typeof option === "string") return option || "";
    const { dba_name, primary_name, license_number } = option;

    const displayName = dba_name ? dba_name : primary_name;

    return `${displayName} - ${license_number}`.trim();
  };

  const handleAddressChange = (_e, value, reason) => {
    if (reason === "clear" || value == null) {
      setSelectedOption(null);
    } else {
      setSelectedOption(value);
    }
  };

  const handleSelectContractor = () => {
    setFoundContractor(selectedOption);
    setShowAddInput(true);
    setShowLicenseInput(false);

    formik?.setValues((prevValue) => ({
      ...prevValue,
      id: selectedOption?.id,
      firstName: selectedOption?.first_name,
      lastName: selectedOption?.last_name,
      businessName: selectedOption?.dba_name,
      licenseType: selectedOption?.license_type,
      licenseNumber: selectedOption?.license_number,
      rank: selectedOption?.rank,
      status: selectedOption?.status?.secondary,
      expires: selectedOption?.reg_date?.expiration,
      mainAddress: `${selectedOption?.address_parts?.part_1} ${selectedOption?.address_parts?.part_2} ${selectedOption?.address_parts?.part_3} ${selectedOption?.address_parts?.city}, ${selectedOption?.address_parts?.state} ${selectedOption?.address_parts?.zip}`,
      mainAddressCounty: selectedOption?.county,
      address1: selectedOption?.address_parts?.part_1,
      address2: selectedOption?.address_parts?.part_2,
      address3: selectedOption?.address_parts?.part_3,
      city: selectedOption?.address_parts?.city,
      state: selectedOption?.address_parts?.state,
      zip: selectedOption?.address_parts?.zip,
      email: selectedOption?.email ? selectedOption?.email : "",
    }));
  };

  useEffect(() => {
    if (inputValue.trim() === lastFetchedContractorRef.current) return;
    lastFetchedContractorRef.current = inputValue.trim();

    if (suppressFetchRef.current) {
      suppressFetchRef.current = false;
      return;
    }

    if (!inputValue || typeof inputValue !== "string") return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      (async () => {
        try {
          setFetchLoading(true);
          const payload = await dispatch(searchPPContractor(inputValue.trim()));
          setContractorOptions(Array.isArray(payload) ? payload : []);
        } catch (e) {
          setContractorOptions([]);
        } finally {
          setFetchLoading(false);
        }
      })();
    }, 1000);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [inputValue, dispatch]);

  return (
    <Box sx={{ zIndex: 1, mx: 3, backgroundColor: "#fff" }}>
      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={contractorOptions ?? []}
        value={selectedOption}
        inputValue={inputValue}
        getOptionLabel={(opt) => (opt ? formatContractorOption(opt) : "")}
        isOptionEqualToValue={(opt, val) =>
          !!opt &&
          !!val &&
          (opt.parcel_number ?? opt.id) === (val.parcel_number ?? val.id)
        }
        noOptionsText={
          fetchLoading
            ? "Searching contractor..."
            : inputValue?.trim() !== ""
              ? "No contractor found"
              : ""
        }
        onInputChange={(_e, newValue, reason) => {
          const cleaned = (newValue ?? "").toUpperCase();
          setInputValue(cleaned);

          // manual delete or clear button should clear the selection
          if (reason === "clear" || cleaned === "") {
            setSelectedOption(null);
            // optional: close menu & wipe options to avoid a fetch
            setOpen(false);
            setContractorOptions([]);
          }
        }}
        onChange={handleAddressChange}
        renderOption={(props, option, { index }) => {
          const { dba_name, primary_name, rank, county, license_number, id } =
            option;

          const displayName = dba_name ? dba_name : primary_name;
          const combined =
            `${displayName}-${license_number}-${rank}-${county}`.trim();

          return (
            <li {...props} key={id}>
              {combined}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search Contractor"
            autoComplete="off"
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                autoComplete: "new-password",
              },
            }}
            sx={{
              userSelect: "none",
              WebkitUserSelect: "none",
              MozUserSelect: "none",
              MsUserSelect: "none",
            }}
          />
        )}
        slots={{ popper: CustomPopper }}
        sx={{ mt: 1 }}
        slotProps={{
          listbox: {
            style: {
              maxHeight: "200px", // Ensure the height is constrained
              overflowY: "auto", // Ensure scrolling
            },
          },
        }}
      />
      <Box
        className="fadeIn"
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 5,
        }}
      >
        {selectedOption && (
          <Button
            onClick={handleSelectContractor}
            color="success"
            sx={{ minWidth: "158px" }}
            variant="contained"
          >
            Select Contractor
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default LicenseAuto;
